import { useEffect, useState } from 'react'

import { Pagination, Stack, SxProps, TablePagination } from '@mui/material'
import Box from '@mui/material/Box/Box'
import { DataGrid, GridColDef, GridValidRowModel, useGridApiContext } from '@mui/x-data-grid'

type CustomPaginationProps = {
  total?: number
  page?: number
  pageSize?: number
  pageSizeOptions?: number[]
}
function CustomPagination(props: CustomPaginationProps) {
  const { total = 0, page = 1, pageSizeOptions = [12, 24, 48] } = props
  const pageSize = props.pageSize || pageSizeOptions[0]
  const apiRef = useGridApiContext()

  return (
    <Stack
      direction={{ sm: 'column', md: 'row' }}
      justifyContent="end"
      alignItems="center"
      spacing={2}
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          mb: 1,
          mx: 'auto',
        },
      })}
    >
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_event, newPage) => apiRef.current.setPage(newPage)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={pageSizeOptions}
        onRowsPerPageChange={(event) => apiRef.current.setPageSize(Number(event.target.value))}
        backIconButtonProps={{
          sx: { display: 'none' },
        }}
        nextIconButtonProps={{
          sx: { display: 'none' },
        }}
      />
      <Pagination
        color="primary"
        count={Math.ceil(total / pageSize)}
        page={page}
        onChange={(_event, newPage) => apiRef.current.setPage(newPage - 1)}
      />
    </Stack>
  )
}

type DataTableProps = {
  data: GridValidRowModel[]
  total?: number
  columns: GridColDef[]
  // controls both pagination and sorting
  paginationMode?: 'client' | 'server'
  page?: number
  pageSize?: number
  pageSizeOptions?: number[]
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  onOrderChange?: (newOrder: { field?: string; sort?: 'asc' | 'desc' }) => void
  onRowClick?: (row: GridValidRowModel) => void
  sx?: SxProps
}

export default function DataTable(props: DataTableProps) {
  const {
    data,
    columns,
    paginationMode = 'client',
    onPageChange = (newPage) => {
      setInternalPage(newPage)
    },
    pageSizeOptions = [12, 24, 48],
    onPageSizeChange = (newPageSize) => {
      setInternalPage(1) // prevents MUI from throwing an error for a page being out of range
      setInternalPageSize(newPageSize)
    },
    orderBy,
    orderDirection,
    onOrderChange,
    onRowClick,
    sx,
  } = props
  const total = props.total || data?.length

  // doing this so that we can have a controlled and uncontrolled version of the page and pageSize depending on paginationMode
  // client mode will control its own state while server forces the internal state to match the props
  const [internalPage, setInternalPage] = useState(props.page || 1)
  useEffect(() => {
    if (paginationMode === 'client' || !props.page) return
    setInternalPage(props.page)
  }, [props.page])
  const [internalPageSize, setInternalPageSize] = useState(props.pageSize || pageSizeOptions[0])
  useEffect(() => {
    if (paginationMode === 'client' || !props.pageSize) return
    setInternalPageSize(props.pageSize)
  }, [props.pageSize])

  return (
    <Box style={{ width: '100%' }} sx={sx}>
      <DataGrid
        autoHeight
        rows={data}
        getRowId={(row) => {
          // combine the values of all the columns to make a unique id
          return columns.reduce((acc, col) => {
            return `${acc}${JSON.stringify(row[col.field])}`
          }, '')
        }}
        rowCount={total}
        columns={columns}
        disableColumnMenu // doesn't pay well with parent component state so disabling for now
        disableSelectionOnClick={false}
        hideFooterSelectedRowCount
        checkboxSelection={false}
        paginationMode={paginationMode}
        sortingMode={paginationMode}
        page={internalPage - 1} // have to adjust since mui is 0 based but that is a bit unusual for pagination
        pageSize={internalPageSize}
        rowsPerPageOptions={pageSizeOptions}
        onPageChange={(newPage) => {
          if (typeof onPageChange !== 'function') return
          onPageChange(newPage + 1) // have to adjust since mui is 0 based but that is a bit unusual for pagination
        }}
        onPageSizeChange={onPageSizeChange}
        sortModel={orderBy ? [{ field: orderBy, sort: orderDirection }] : undefined}
        onSortModelChange={(newSort) => {
          if (typeof onOrderChange !== 'function') return

          if (!newSort || newSort.length === 0) {
            if (orderBy && orderDirection) {
              const newDirection = orderDirection === 'asc' ? 'desc' : 'asc'
              onOrderChange({
                field: orderBy,
                sort: newDirection,
              })
            }
            return
          }

          const newField = newSort[0]?.field
          const newSortDirection = newSort[0]?.sort

          if (newField === orderBy && orderDirection) {
            const toggledDirection = orderDirection === 'asc' ? 'desc' : 'asc'
            onOrderChange({
              field: newField,
              sort: toggledDirection,
            })
          } else if (newField) {
            onOrderChange({
              field: newField,
              sort: newSortDirection || 'asc',
            })
          }
        }}
        onRowClick={(params, event) => {
          const target = event.target as HTMLElement
          if (
            target.closest('button') ||
            target.closest('a') ||
            target.closest('[role="button"]') ||
            target.closest('.MuiIconButton-root')
          ) {
            return
          }
          const selection = window.getSelection()
          if (selection && selection.toString().length > 0) {
            return
          }
          if (typeof onRowClick === 'function') {
            onRowClick(params.row)
          }
        }}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(0, 124, 145, 0.12)',
          borderRadius: 2,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(0, 124, 145, 0.08)',
            color: '#263238', // midnightSlate
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F5F7FA',
            borderBottom: '2px solid rgba(0, 124, 145, 0.12)',
            '& .MuiDataGrid-columnHeader': {
              color: '#263238', // midnightSlate
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:focus, &:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: '#263238',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              '& .MuiIconButton-root': {
                color: '#007C91', // tealBlue
                '&:hover': {
                  backgroundColor: '#E0F7FA',
                },
              },
            },
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#F0F9FA !important',
              cursor: onRowClick ? 'pointer' : 'default',
            },
            '&.Mui-selected': {
              backgroundColor: '#E0F7FA !important',
              '&:hover': {
                backgroundColor: '#D0F0F5 !important',
              },
            },
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within, & .MuiDataGrid-root:focus, & .MuiDataGrid-root:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeaderTitleContainer:focus, & .MuiDataGrid-columnHeaderTitleContainer:focus-within':
            {
              outline: 'none',
            },
          ...(onRowClick && {
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell p, & .MuiDataGrid-row:hover .MuiDataGrid-cell span, & .MuiDataGrid-row:hover .MuiDataGrid-cell div, & .MuiDataGrid-row:hover .MuiDataGrid-cell h1, & .MuiDataGrid-row:hover .MuiDataGrid-cell h2, & .MuiDataGrid-row:hover .MuiDataGrid-cell h3, & .MuiDataGrid-row:hover .MuiDataGrid-cell h4, & .MuiDataGrid-row:hover .MuiDataGrid-cell h5, & .MuiDataGrid-row:hover .MuiDataGrid-cell h6':
              {
                cursor: 'text',
              },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="imageURL"]': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="imageURL"] *': {
              cursor: 'pointer',
            },
          }),
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(0, 124, 145, 0.12)',
            backgroundColor: '#F5F7FA',
          },
          '& .MuiTablePagination-root': {
            color: '#263238',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: '#263238',
            },
          },
          '& .MuiPagination-root': {
            '& .MuiPaginationItem-root': {
              color: '#263238',
              '&.Mui-selected': {
                backgroundColor: '#007C91',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#006B7D',
                },
              },
              '&:hover': {
                backgroundColor: '#E0F7FA',
              },
            },
          },
          ...sx,
        }}
        components={{
          Pagination: CustomPagination,
        }}
        componentsProps={{
          pagination: {
            total,
            page: internalPage,
            pageSize: internalPageSize,
            pageSizeOptions,
          },
        }}
      />
    </Box>
  )
}
